/**
 * Global command palette (⌘K / Ctrl+K).
 *
 * Provides quick navigation + theme toggle + sign out. Future: tenant /
 * payment search wired into the API.
 */
import { useNavigate } from "react-router-dom";
import {
  ArrowDownToLine,
  Building2,
  Inbox,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Monitor,
  Moon,
  ScrollText,
  Send,
  Sun,
  Users,
  Wallet,
  Webhook,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useCommandPalette } from "@/lib/command-palette-context";
import { useAuth } from "@/lib/use-auth";
import { useTheme } from "@/lib/theme-context";

export function CommandPalette() {
  const { isOpen, close } = useCommandPalette();
  const { setTheme } = useTheme();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.role === "super";

  function go(to: string) {
    close();
    navigate(to);
  }

  async function onSignOut() {
    close();
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    // Open is controlled by ``isOpen`` (toggled via ⌘K + topbar trigger);
    // only honour the *close* signal from cmdk (Esc, overlay click).
    <CommandDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <CommandInput placeholder="Type a command or search…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Navigation">
          <CommandItem onSelect={() => go("/")}>
            <LayoutDashboard /> <span>Dashboard</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/payment-intents")}>
            <Send /> <span>Payment intents</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/inbound-payments")}>
            <ArrowDownToLine /> <span>Inbound payments</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/balance-queries")}>
            <Wallet /> <span>Balance queries</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/callback-inbox")}>
            <Inbox /> <span>Callback inbox</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/webhook-outbox")}>
            <Webhook /> <span>Webhook outbox</span>
          </CommandItem>
          {isSuper ? (
            <CommandItem onSelect={() => go("/audit-log")}>
              <ScrollText /> <span>Audit log</span>
            </CommandItem>
          ) : null}
          <CommandItem onSelect={() => go("/tenants")}>
            <Building2 /> <span>Tenants</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/operators")}>
            <Users /> <span>Operators</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Theme">
          <CommandItem onSelect={() => { setTheme("light"); close(); }}>
            <Sun /> <span>Light</span>
          </CommandItem>
          <CommandItem onSelect={() => { setTheme("dark"); close(); }}>
            <Moon /> <span>Dark</span>
          </CommandItem>
          <CommandItem onSelect={() => { setTheme("system"); close(); }}>
            <Monitor /> <span>System</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Account">
          <CommandItem onSelect={() => go("/change-password")}>
            <KeyRound /> <span>Change password</span>
          </CommandItem>
          <CommandItem onSelect={onSignOut}>
            <LogOut /> <span>Sign out</span>
            <CommandShortcut>↵</CommandShortcut>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
